import { format, isFuture } from "date-fns";
import sanityClient from "@sanity/client";

/**
 * Get the local menu with active states
 * @param {object} pageContext
 * @param {array|undefined} pageContext.localMenu
 * @param {string} path
 * @returns array
 */
export function getLocalMenu(pageContext) {
  const localMenu = pageContext?.localMenu || [];
  const path = pageContext?.pathUrl || "";

  // Set active states for menu items at all levels
  function setActiveState(menuItems) {
    let isActive = false;
    menuItems.forEach((item) => {
      if (item.menuItems) {
        // Recursively set active states for sub-menu items
        item.isActive = setActiveState(item.menuItems);
      } else {
        item.isActive = path === item.url;
      }
      isActive = isActive || item.isActive;
    });
    return isActive;
  }

  setActiveState(localMenu);
  return localMenu;
}

export function cn(...args) {
  return args.filter(Boolean).join(" ");
}

export function mapEdgesToNodes(data) {
  if (!data.edges) return [];
  return data.edges.map((edge) => edge.node);
}

export function filterOutDocsWithoutSlugs({ slug }) {
  return (slug || {}).current;
}

export function filterOutDocsPublishedInTheFuture({ publishedAt }) {
  return !isFuture(publishedAt);
}

export function getBlogUrl(publishedAt, slug) {
  return `/blog/${format(publishedAt, "YYYY/MM")}/${slug.current || slug}/`;
}

export function buildImageObj(source = { asset: {} }) {
  const imageObj = {
    asset: { _ref: source.asset._ref || source.asset._id },
  };

  if (source.crop) imageObj.crop = source.crop;
  if (source.hotspot) imageObj.hotspot = source.hotspot;

  return imageObj;
}

export function toPlainText(blocks) {
  if (!blocks) {
    return "";
  }
  return blocks
    .map((block) => {
      if (block._type !== "block" || !block.children) {
        return "";
      }
      return block.children.map((child) => child.text).join("");
    })
    .join("\n\n");
}

export function getYouTubeId(url) {
  if (/youtu\.?be/.test(url)) {
    var i,
      patterns = [
        /youtu\.be\/([^#\&\?]{11})/, // youtu.be/<id>
        /\?v=([^#\&\?]{11})/, // ?v=<id>
        /\&v=([^#\&\?]{11})/, // &v=<id>
        /embed\/([^#\&\?]{11})/, // embed/<id>
        /\/v\/([^#\&\?]{11})/, // /v/<id>
      ];
    for (i = 0; i < patterns.length; ++i) {
      if (patterns[i].test(url)) {
        return patterns[i].exec(url)[1];
      }
    }
  }
  return null;
}

/**
 * Gets the news path used for page generate and linking between pages
 * @param {object} section
 * @returns {string}
 */
export function getNewsPath({ section }) {
  return section ? section.urlPrefix + "news" : "";
}

/**
 * Async Social Post Actions
 * @type {{LOAD_MORE_SOCIAL_POSTS: (function({dispatch: *, getState: *}): function(): Promise<void>)}}
 */
export const asyncSocialPostActions = {
  LOAD_MORE_SOCIAL_POSTS:
    ({ dispatch, getState }) =>
    async () => {
      const state = getState();
      const start = state.currentPage * state.itemsPerPage;
      const end = (state.currentPage + 1) * state.itemsPerPage;
      const currentPage = state.currentPage + 1;
      const postQuery = `*[_type=="socialPost"]|order(_createdAt desc){caption,image{asset->{_id,metadata{dimensions}}},postHandle,tags,handles,_id,videoMp4{asset->{url}},songLink,songTitle,socialPostType->{handleUrl,tagUrl,type}}[${start}...${end}]`;
      const response = await client.fetch(postQuery, {});
      const socialPosts = [...state.socialPosts, ...response];
      dispatch({ type: "LOAD_MORE", data: { socialPosts, currentPage } });
    },
};

/**
 * Async Post Actions
 * @type {{LOAD_MORE_POSTS: (function({dispatch: *, getState: *}): function(): Promise<void>)}}
 */
export const asyncPostActions = {
  LOAD_MORE_POSTS:
    ({ dispatch, getState }) =>
    async () => {
      const state = getState();
      const start =
        state.currentPage * state.itemsPerPage + (state.offset || 0);
      const end =
        (state.currentPage + 1) * state.itemsPerPage + (state.offset || 0);
      const currentPage = state.currentPage + 1;
      let filterIds = "";
      if (Array.isArray(state.filterIds) && state.filterIds.length) {
        filterIds = `&& ${state.filterIds
          .map((id) => `_id != "${id}"`)
          .join(" && ")}`;
      }
      const filterDrafts = '&& !(_id in path("drafts.**"))';
      const postQuery = `*[_type=="newsPost" ${filterIds} ${filterDrafts} ${
        state.noSpecialCategories
          ? '&& !(categories[]->categorySlug.current match "rollins-in-the-news") && !(categories[]->categorySlug.current match "highlights")'
          : ""
      } ${
        state.categoryId
          ? `&& categories[]._ref match "${state.categoryId}"`
          : ""
      } && sections[]._ref match "${
        state.sectionId
      }"]|order(publishedAt desc){_id,publishedAt,slug,mainImage,intro,title,videoID,categories[]->{categorySlug},externalLink}[${start}...${end}]`;

      const response = await client.fetch(postQuery, {});
      const mappedResponse = response.map((item) => {
        if (item.mainImage && item.mainImage.asset) {
          item.mainImage.asset._id = item.mainImage.asset._ref;
        }
        if (item.intro) {
          item._rawIntro = item.intro;
        }

        return item;
      });
      const posts = [...state.posts, ...mappedResponse];
      dispatch({ type: "LOAD_MORE", data: { posts, currentPage } });
    },
};

/**
 * Client Setup
 * @type {SanityClient}
 */
export const client = sanityClient({
  projectId: process.env.GATSBY_SANITY_STUDIO_API_PROJECT_ID,
  dataset: process.env.GATSBY_SANITY_STUDIO_API_DATASET,
  apiVersion: "2021-08-31",
  token: process.env.GATSBY_TOKEN,
  useCdn: true,
});

/**
 * Reducer
 * @param state
 * @param action
 * @returns {*}
 */
export function loadMoreReducer(state, { type, data }) {
  switch (type) {
    case "LOAD_MORE":
      return { ...state, ...data };
    default:
      throw new Error();
  }
}

/**
 * Reducer
 * @param state
 * @param action
 * @returns {*}
 */
export function menuReducer(state, { type, data }) {
  switch (type) {
    case "UPDATE_MEGA_MENU":
      return { ...state, ...data };
    default:
      throw new Error();
  }
}

/**
 * Reducer
 * @param state
 * @param action
 * @returns {*}
 */
export function hamburgerMenuReducer(state, { type, data }) {
  switch (type) {
    case "UPDATE_HAMBURGER_MENU":
      return { ...state, ...data };
    default:
      throw new Error();
  }
}

/**
 * Performs a deep merge of objects and returns new object. Does not modify
 * objects (immutable) and merges arrays via concatenation.
 *
 * @param {...object} objects - Objects to merge
 * @returns {object} New object with merged key/values
 */
export function mergeDeep(...objects) {
  const isObject = (obj) => obj && typeof obj === "object";

  return objects.reduce((prev, obj) => {
    Object.keys(obj).forEach((key) => {
      const pVal = prev[key];
      const oVal = obj[key];

      if (Array.isArray(pVal) && Array.isArray(oVal)) {
        prev[key] = pVal.concat(...oVal);
      } else if (isObject(pVal) && isObject(oVal)) {
        prev[key] = mergeDeep(pVal, oVal);
      } else {
        prev[key] = oVal;
      }
    });

    return prev;
  }, {});
}
